import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

export interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  food_type_id: string | null;
  daily_menu_id: string;
  food_type?: {
    name: string;
    display_order: number | null;
  };
}

export interface DailyMenu {
  id: string;
  restaurant_id: string;
  dine_in_price: number | null;
  delivery_price: number | null;
  takeaway_price: number | null;
  menu_type_id: string | null;
  display_order: number | null;
  menu_type?: {
    name: string;
    display_order: number | null;
  };
}

export interface MenuWithItems {
  menu: DailyMenu;
  items: MenuItem[];
}

export type MenusByRestaurant = Map<string, MenuWithItems[]>;

async function fetchMenus(menuDate: Date, restaurantIds: string[]): Promise<MenusByRestaurant> {
  if (restaurantIds.length === 0) {
    return new Map();
  }

  const today = format(menuDate, "yyyy-MM-dd");

  // Embedded available items remove the menu -> item network waterfall.
  const { data: menusData, error } = await supabase
    .from("daily_menus")
    .select(`
      id, restaurant_id, dine_in_price, delivery_price, takeaway_price,
      menu_type_id, display_order,
      menu_types (name, display_order),
      menu_items (
        id, name, description, price, food_type_id, daily_menu_id,
        food_types (name, display_order)
      )
    `)
    .in("restaurant_id", restaurantIds)
    .eq("menu_date", today)
    .eq("is_active", true)
    .eq("menu_items.is_available", true);

  if (error) throw error;
  const result: MenusByRestaurant = new Map();
  for (const row of menusData ?? []) {
    const { menu_items, menu_types, ...menu } = row;
    const items: MenuItem[] = (menu_items ?? []).map(({ food_types, ...item }) => ({
      ...item,
      food_type: food_types ?? undefined,
    }));
    if (items.length === 0) continue;
    const restaurantMenus = result.get(menu.restaurant_id) ?? [];
    restaurantMenus.push({ menu: { ...menu, menu_type: menu_types ?? undefined }, items });
    result.set(menu.restaurant_id, restaurantMenus);
  }

  // Sort menus within each restaurant
  for (const [, menus] of result) {
    menus.sort((a, b) => {
      const menuTypeOrderA = a.menu.menu_type?.display_order ?? 999;
      const menuTypeOrderB = b.menu.menu_type?.display_order ?? 999;
      if (menuTypeOrderA !== menuTypeOrderB) {
        return menuTypeOrderA - menuTypeOrderB;
      }
      const displayOrderA = a.menu.display_order ?? 999;
      const displayOrderB = b.menu.display_order ?? 999;
      return displayOrderA - displayOrderB;
    });
  }

  return result;
}

export function useAllDailyMenus(menuDate: Date, restaurantIds: string[]) {
  // Stabil query key létrehozása
  const dateKey = format(menuDate, "yyyy-MM-dd");
  const idsKey = useMemo(() => restaurantIds.slice().sort().join(","), [restaurantIds]);

  const { data, isLoading } = useQuery({
    queryKey: ["daily-menus", dateKey, idsKey, "embedded-v1"],
    queryFn: () => fetchMenus(menuDate, restaurantIds),
    enabled: restaurantIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 perc - gyorsabb frissítés
    gcTime: 15 * 60 * 1000, // 15 perc cache
    refetchOnWindowFocus: true, // Ablakváltáskor frissít
  });

  return { 
    menusByRestaurant: data ?? new Map(), 
    loading: isLoading 
  };
}
